import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, StickyNote, ListChecks, AlertTriangle, X, MailWarning, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";

/**
 * NotificationsBell — sidebar bell that aggregates the four "someone needs
 * me" signals: notes tagged to me, open actionables assigned to me,
 * (staff/admin) unreviewed conflicts, and undelivered emails.
 *
 * Iter 74 — users can now clear items. Dismissals are stored server-side
 * per (user, item_type, item_id) so the underlying source (note, conflict,
 * etc.) stays intact but the bell honours the clear.
 */
export default function NotificationsBell() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ tagged_notes: [], actionables: [], conflicts: [], undelivered_emails: [] });
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setData(data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // refresh every minute
    return () => clearInterval(t);
  }, []);

  const total = (data.tagged_notes?.length || 0) + (data.actionables?.length || 0) + (data.conflicts?.length || 0) + (data.undelivered_emails?.length || 0);
  const jump = (path) => { setOpen(false); nav(path); };

  // Optimistically remove the item from local state, then persist. Server
  // never errors on double-dismiss so a network hiccup won't resurrect it.
  const dismiss = async (item_type, item_id, e) => {
    e?.stopPropagation?.();
    const key = { tagged_note: "tagged_notes", actionable: "actionables",
                  conflict: "conflicts", undelivered_email: "undelivered_emails" }[item_type];
    setData((d) => ({ ...d, [key]: (d[key] || []).filter((x) => x.id !== item_id) }));
    try {
      await api.post("/notifications/dismiss", { item_type, item_id });
    } catch { /* silent — will re-appear on next refresh if it didn't stick */ }
  };

  const clearAll = async () => {
    if (total === 0) return;
    setClearing(true);
    try {
      await api.post("/notifications/dismiss-all");
      setData({ tagged_notes: [], actionables: [], conflicts: [], undelivered_emails: [] });
    } catch { /* silent */ }
    finally { setClearing(false); }
  };

  return (
    <>
      <button
        data-testid="notifications-bell"
        onClick={() => { setOpen(true); load(); }}
        className="relative w-9 h-9 rounded-md border border-white/10 grid place-items-center text-zinc-300 hover:text-white hover:border-white/25 hover:bg-white/5 transition-colors"
        title="Notifications"
      >
        <Bell className="w-4 h-4" />
        {total > 0 && (
          <span
            data-testid="notifications-badge"
            className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-gold text-black text-[10px] font-semibold grid place-items-center leading-none"
          >
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-[80]" data-testid="notifications-drawer">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <aside className="absolute right-0 top-0 h-full w-full max-w-md bg-[#0F0F0F] border-l border-white/10 flex flex-col animate-in slide-in-from-right duration-150">
            <header className="px-5 py-4 border-b border-white/10 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.24em] text-gold">Notifications</div>
                <h2 className="mt-0.5 font-display text-2xl leading-tight">Needs your attention</h2>
                <div className="text-[11px] text-zinc-500 mt-0.5">{total === 0 ? "You're all caught up." : `${total} item${total === 1 ? "" : "s"}`}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {total > 0 && (
                  <button
                    data-testid="notifications-clear-all"
                    onClick={clearAll}
                    disabled={clearing}
                    title="Clear every item in the bell"
                    className="h-8 px-2.5 rounded border border-white/10 text-[11px] text-zinc-300 hover:text-white hover:bg-white/5 inline-flex items-center gap-1.5 disabled:opacity-40"
                  >
                    <CheckCheck className="w-3.5 h-3.5" /> Clear all
                  </button>
                )}
                <button data-testid="notifications-drawer-close" onClick={() => setOpen(false)} className="w-8 h-8 grid place-items-center rounded border border-white/10 text-zinc-400 hover:text-white hover:bg-white/5"><X className="w-4 h-4" /></button>
              </div>
            </header>
            <div className="flex-1 overflow-y-auto p-4 space-y-5">
              {loading && total === 0 && <div className="text-sm text-zinc-500">Loading…</div>}

              {(data.tagged_notes?.length || 0) > 0 && (
                <section data-testid="notif-tagged-notes">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2 inline-flex items-center gap-1.5"><StickyNote className="w-3 h-3" /> Notes tagged to you</div>
                  <div className="space-y-2">
                    {data.tagged_notes.map((n) => (
                      <NotifRow
                        key={n.id}
                        testid={`notif-note-${n.id}`}
                        onClick={() => jump(`/notes-hub?tab=production`)}
                        onDismiss={(e) => dismiss("tagged_note", n.id, e)}
                      >
                        <div className="text-sm text-zinc-100 leading-tight">{n.title}</div>
                        <div className="text-[11px] text-zinc-500 mt-1 line-clamp-2">{n.content}</div>
                        <div className="text-[10px] text-zinc-600 mt-1.5">{n.created_by_name} · {fmtDate(n.created_at)}</div>
                      </NotifRow>
                    ))}
                  </div>
                </section>
              )}

              {(data.actionables?.length || 0) > 0 && (
                <section data-testid="notif-actionables">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2 inline-flex items-center gap-1.5"><ListChecks className="w-3 h-3" /> Open actionables for you</div>
                  <div className="space-y-2">
                    {data.actionables.map((a) => (
                      <NotifRow
                        key={a.id}
                        testid={`notif-actionable-${a.id}`}
                        onClick={() => jump(`/rehearsals?tab=actionables`)}
                        onDismiss={(e) => dismiss("actionable", a.id, e)}
                      >
                        <div className="text-sm text-zinc-100 leading-tight">{a.description}</div>
                        <div className="text-[10px] text-zinc-600 mt-1.5">{a.status} · {fmtDate(a.created_at)}</div>
                      </NotifRow>
                    ))}
                  </div>
                </section>
              )}

              {(data.conflicts?.length || 0) > 0 && (
                <section data-testid="notif-conflicts">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2 inline-flex items-center gap-1.5"><AlertTriangle className="w-3 h-3" /> {user.role === "cast" ? "Your pending conflicts" : "Unreviewed conflicts"}</div>
                  <div className="space-y-2">
                    {data.conflicts.map((c) => (
                      <NotifRow
                        key={c.id}
                        testid={`notif-conflict-${c.id}`}
                        onClick={() => jump("/conflicts")}
                        onDismiss={(e) => dismiss("conflict", c.id, e)}
                      >
                        <div className="text-sm text-zinc-100 leading-tight">{c.user_name} · {fmtDate(c.date)}</div>
                        <div className="text-[11px] text-zinc-500 mt-1 line-clamp-2">{c.reason || "No reason provided"}</div>
                      </NotifRow>
                    ))}
                  </div>
                </section>
              )}

              {(data.undelivered_emails?.length || 0) > 0 && (
                <section data-testid="notif-undelivered-emails">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-red-300 mb-2 inline-flex items-center gap-1.5"><MailWarning className="w-3 h-3" /> Emails that didn&apos;t reach the recipient</div>
                  <div className="space-y-2">
                    {data.undelivered_emails.slice(0, 8).map((e) => (
                      <NotifRow
                        key={e.id}
                        testid={`notif-undelivered-${e.id}`}
                        tone="danger"
                        onClick={() => jump("/email-log?status=failed")}
                        onDismiss={(ev) => dismiss("undelivered_email", e.id, ev)}
                      >
                        <div className="text-sm text-zinc-100 leading-tight truncate">{e.subject}</div>
                        <div className="text-[11px] text-red-300 mt-1 truncate">{e.recipient_name || e.to} · {e.error || "provider refused"}</div>
                        <div className="text-[10px] text-zinc-600 mt-0.5">{fmtDate(e.created_at)}</div>
                      </NotifRow>
                    ))}
                    {data.undelivered_emails.length > 8 && (
                      <button onClick={() => jump("/email-log?status=failed")} className="w-full text-center text-[11px] text-red-300 hover:text-red-200 py-1">
                        See all {data.undelivered_emails.length} undelivered in Email audit →
                      </button>
                    )}
                  </div>
                </section>
              )}

              {total === 0 && !loading && (
                <div className="rounded-lg border border-dashed border-white/10 p-8 text-center">
                  <Bell className="w-8 h-8 mx-auto text-zinc-600" />
                  <div className="mt-2 text-sm text-zinc-400">Nothing new right now.</div>
                  <div className="text-[11px] text-zinc-600 mt-1">You&apos;ll see tagged notes, assigned tasks, and unreviewed conflicts here.</div>
                </div>
              )}
            </div>
          </aside>
        </div>
      )}
    </>
  );
}

// Single-row wrapper: the outer button navigates, the inner X calls
// stopPropagation and dismisses so the row can host both actions cleanly.
function NotifRow({ testid, tone, onClick, onDismiss, children }) {
  const danger = tone === "danger";
  return (
    <div className={`relative rounded-lg border ${danger ? "border-red-500/30 bg-red-500/5" : "border-white/10 bg-[#121212]"}`}>
      <button
        data-testid={testid}
        onClick={onClick}
        className={`w-full text-left p-3 pr-9 rounded-lg transition-colors ${danger ? "hover:border-red-500/50 hover:bg-red-500/10" : "hover:border-gold/40 hover:bg-gold/5"}`}
      >
        {children}
      </button>
      <button
        data-testid={`${testid}-clear`}
        onClick={onDismiss}
        title="Clear this notification"
        aria-label="Clear this notification"
        className="absolute top-1.5 right-1.5 w-7 h-7 grid place-items-center rounded text-zinc-500 hover:text-white hover:bg-white/10"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
